import React, { useEffect, useRef, useMemo } from 'react';
import { 
  Box, 
  TextField, 
  FormControl,
  InputLabel, 
  Select, 
  MenuItem,
  SelectChangeEvent,
  InputAdornment,
} from '@mui/material';
import { OnlinePaymentData, OnlinePaymentMode } from '../../collectPayment.types';
import { ONLINE_PAYMENT_MODES } from '../../collectPayment.constants';
import useCurrency from '../../../../../components/CurrencyProvider';

interface OnlinePaymentFormProps {
  data: OnlinePaymentData;
  onChange: (data: OnlinePaymentData) => void;
  errors?: {
    amount?: string;
    paymentMode?: string;
    transactionNumber?: string;
  };
  remainingAmount?: number;
}

const OnlinePaymentForm: React.FC<OnlinePaymentFormProps> = ({
  data,
  onChange,
  errors,
  remainingAmount,
}) => {
  const { currencySymbol } = useCurrency();
  const prevRemainingRef = useRef(remainingAmount);
  const onChangeRef = useRef(onChange);

  // Keep refs updated with latest values
  onChangeRef.current = onChange;

  // Auto-populate amount with remaining amount when:
  // 1. Form is first rendered with no amount, OR
  // 2. Remaining amount changes (after adding a payment) and current amount is 0
  useEffect(() => {
    const remainingChanged = prevRemainingRef.current !== remainingAmount;
    prevRemainingRef.current = remainingAmount;
    
    if (remainingAmount && remainingAmount > 0 && !data.amount) {
      onChangeRef.current({ ...data, amount: remainingAmount });
    } else if (remainingChanged && remainingAmount && remainingAmount > 0 && data.amount === 0) {
      onChangeRef.current({ ...data, amount: remainingAmount });
    }
  }, [remainingAmount, data]);

  const handleModeChange = (event: SelectChangeEvent<string>) => {
    onChange({ ...data, paymentMode: event.target.value as OnlinePaymentMode });
  };

  // Real-time validation: check if amount exceeds remaining
  const amountError = useMemo(() => {
    if (errors?.amount) return errors.amount;
    if (remainingAmount !== undefined && data.amount > remainingAmount) {
      return 'Amount cannot exceed remaining balance';
    }
    return undefined;
  }, [errors?.amount, data.amount, remainingAmount]);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Amount"
          type="number"
          value={data.amount || ''}
          onChange={(e) => {
            const value = parseFloat(e.target.value) || 0;
            onChange({ ...data, amount: value });
          }}
          placeholder="0.00"
          error={!!amountError}
          helperText={amountError}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  {currencySymbol}
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormControl fullWidth size="small" error={!!errors?.paymentMode}>
          <InputLabel>Payment Mode</InputLabel>
          <Select
            value={data.paymentMode}
            onChange={handleModeChange}
            label="Payment Mode"
          >
            {ONLINE_PAYMENT_MODES.map((mode) => (
              <MenuItem key={mode.value} value={mode.value}>
                {mode.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Transaction Number"
          value={data.transactionNumber || ''}
          onChange={(e) => onChange({ ...data, transactionNumber: e.target.value })}
          placeholder="Enter Transaction Number"
          error={!!errors?.transactionNumber}
          helperText={errors?.transactionNumber}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Notes"
          multiline
          minRows={2}
          maxRows={4}
          value={data.notes || ''}
          onChange={(e) => onChange({ ...data, notes: e.target.value })}
          placeholder="Enter Note"
        />
      </Box>
    </Box>
  );
};

export default OnlinePaymentForm;
